/**
 * useBriefAdmin — encapsulates all Brief-of-the-Week state and handlers
 * for AdminDashboard. Extracted as part of P3-3 refactor.
 */
import { useState, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { BACKEND_URL } from "../lib/backend";

const API = `${BACKEND_URL}/api`;

export function useBriefAdmin(authHeader) {
  const [brief, setBrief] = useState(null);
  const [briefContent, setBriefContent] = useState("");
  const [briefPhotoUrl, setBriefPhotoUrl] = useState("");
  const [briefPublished, setBriefPublished] = useState(false);
  const [generatingBrief, setGeneratingBrief] = useState(false);
  const [savingBrief, setSavingBrief] = useState(false);
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [socialTexts, setSocialTexts] = useState({});
  const [briefOgPrompt, setBriefOgPrompt] = useState("");
  const [regeneratingBriefOg, setRegeneratingBriefOg] = useState(false);
  const [autoSettings, setAutoSettings] = useState({
    auto_refresh_enabled: false, day_of_week: 0, hour: 8,
    notify_email: "", review_window_hours: 12,
  });
  const [savingAutoSettings, setSavingAutoSettings] = useState(false);
  const [editorComment, setEditorComment] = useState("");
  const [editorUrls, setEditorUrls] = useState([]);
  const [newEditorUrl, setNewEditorUrl] = useState("");
  const [editorNotesActive, setEditorNotesActive] = useState(false);
  const [savingEditorNotes, setSavingEditorNotes] = useState(false);
  const [loadingEditorNotes, setLoadingEditorNotes] = useState(false);
  const photoInputRef = useRef(null);

  // Featured-for-brief list (persistent, editable — mirror of podcast queue)
  const [briefFeatured, setBriefFeatured] = useState([]);
  const [loadingBriefFeatured, setLoadingBriefFeatured] = useState(false);

  const loadBriefFeatured = useCallback(async () => {
    setLoadingBriefFeatured(true);
    try {
      const res = await axios.get(`${API}/admin/brief/featured`, { headers: authHeader() });
      setBriefFeatured(res.data.articles || []);
    } catch { /* silent */ }
    finally { setLoadingBriefFeatured(false); }
  }, [authHeader]);

  const removeBriefFeatured = async (articleId) => {
    try {
      await axios.delete(`${API}/admin/brief/featured/${encodeURIComponent(articleId)}`, { headers: authHeader() });
      setBriefFeatured(prev => prev.filter(a => (a.id || a.originalUrl) !== articleId));
      toast.success("Article removed from brief queue");
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };

  const clearBriefFeatured = async () => {
    try {
      await axios.delete(`${API}/admin/brief/featured`, { headers: authHeader() });
      setBriefFeatured([]);
      toast.success("Brief queue cleared");
    } catch { toast.error("Clear failed"); }
  };

  const loadBrief = useCallback(async () => {
    setLoadingBrief(true);
    try {
      const [briefRes, settingsRes] = await Promise.all([
        axios.get(`${API}/admin/brief`, { headers: authHeader() }),
        axios.get(`${API}/admin/brief/settings`, { headers: authHeader() }),
      ]);
      const b = briefRes.data.brief;
      if (b) {
        setBrief(b);
        setBriefContent(b.content || "");
        setBriefPhotoUrl(b.author_photo_url || "");
        setBriefPublished(b.is_published || false);
        setBriefOgPrompt(b.og_image_prompt || "");
        setSocialTexts(b.social_texts || {});
      }
      if (settingsRes.data) setAutoSettings(settingsRes.data);
    } catch { toast.error("Failed to load brief"); }
    finally { setLoadingBrief(false); }
  }, [authHeader]);

  const loadEditorNotes = useCallback(async () => {
    setLoadingEditorNotes(true);
    try {
      const res = await axios.get(`${API}/admin/brief/editor-notes`, { headers: authHeader() });
      setEditorComment(res.data.comment || "");
      setEditorUrls(res.data.urls || []);
      setEditorNotesActive(res.data.is_active || false);
    } catch (e) { console.error("Failed to load editor notes", e); }
    finally { setLoadingEditorNotes(false); }
  }, [authHeader]);

  const saveEditorNotes = async () => {
    if (!editorComment.trim() && editorUrls.length === 0) {
      toast.error("Add a comment or at least one URL before saving.");
      return;
    }
    setSavingEditorNotes(true);
    try {
      await axios.post(`${API}/admin/brief/editor-notes`, { comment: editorComment, urls: editorUrls }, { headers: authHeader() });
      setEditorNotesActive(true);
      toast.success("Editor's input saved — will be used in the next brief generation.");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingEditorNotes(false); }
  };

  const clearEditorNotes = async () => {
    try {
      await axios.delete(`${API}/admin/brief/editor-notes`, { headers: authHeader() });
      setEditorComment(""); setEditorUrls([]); setEditorNotesActive(false);
      toast.success("Editor's input cleared.");
    } catch { toast.error("Clear failed"); }
  };

  const [addingEditorUrl, setAddingEditorUrl] = useState(false);

  const addEditorUrl = async () => {
    const url = newEditorUrl.trim();
    if (!url) return;
    if (!url.startsWith("http")) { toast.error("Enter a valid URL starting with http(s)"); return; }
    if (editorUrls.includes(url)) { toast.warning("URL already added"); return; }
    setAddingEditorUrl(true);
    try {
      await axios.post(`${API}/admin/force-include-url`, { url, context: "brief" }, { headers: authHeader() });
      setEditorUrls(prev => [...prev, url]);
      setNewEditorUrl("");
      toast.success("Article scraped and added to feed.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not scrape URL — check it's publicly accessible.");
    } finally {
      setAddingEditorUrl(false);
    }
  };

  const saveAutoSettings = async () => {
    setSavingAutoSettings(true);
    try {
      await axios.put(`${API}/admin/brief/settings`, autoSettings, { headers: authHeader() });
      toast.success(autoSettings.auto_refresh_enabled ? "Auto-refresh enabled!" : "Auto-refresh settings saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingAutoSettings(false); }
  };

  const generateBrief = async () => {
    setGeneratingBrief(true);
    try {
      const res = await axios.post(`${API}/admin/brief/generate`, {}, { headers: authHeader() });
      const b = res.data.brief;
      setBrief(b); setBriefContent(b.content || ""); setBriefPublished(b.is_published || false);
      toast.success("Brief generated! Review and publish when ready.");
    } catch (e) { toast.error(e.response?.data?.detail || "Brief generation failed"); }
    finally { setGeneratingBrief(false); }
  };

  const _briefPayload = (isPublished) => ({
    content: briefContent, author_photo_url: briefPhotoUrl, author_name: "Mirko Lalli",
    is_published: isPublished, og_image_prompt: briefOgPrompt, social_texts: socialTexts,
  });

  const saveBrief = async () => {
    setSavingBrief(true);
    try {
      await axios.put(`${API}/admin/brief`, _briefPayload(briefPublished), { headers: authHeader() });
      setBrief(prev => prev ? { ...prev, content: briefContent, author_photo_url: briefPhotoUrl, is_published: briefPublished, og_image_prompt: briefOgPrompt } : null);
      toast.success(briefPublished ? "Brief saved & published!" : "Brief saved as draft.");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSavingBrief(false); }
  };

  const saveDraft = async () => {
    setSavingBrief(true);
    try {
      await axios.put(`${API}/admin/brief`, _briefPayload(false), { headers: authHeader() });
      setBriefPublished(false);
      setBrief(prev => prev ? { ...prev, content: briefContent, is_published: false } : null);
      toast.success("Bozza salvata!");
    } catch (e) { toast.error(e.response?.data?.detail || "Salvataggio fallito"); }
    finally { setSavingBrief(false); }
  };

  const regenerateBriefOg = async () => {
    setRegeneratingBriefOg(true);
    try {
      await axios.put(`${API}/admin/brief`, _briefPayload(briefPublished), { headers: authHeader() });
      const slug = brief?.seo_slug || brief?.slug;
      if (slug) await axios.delete(`${API}/admin/og-cache/brief/${slug}`, { headers: authHeader() });
      setBrief(prev => prev ? { ...prev, og_image_prompt: briefOgPrompt } : null);
      toast.success("Prompt saved — the OG image will be regenerated on the next request.");
    } catch (e) { toast.error(e.response?.data?.detail || "Rigenerazione OG fallita"); }
    finally { setRegeneratingBriefOg(false); }
  };

  const compressImage = (file) => new Promise((resolve, reject) => {
    const MAX_DIM = 400;
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("Canvas toBlob failed")), "image/jpeg", 0.85);
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

  const uploadBriefPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) { toast.error("Use JPG, PNG, WebP, or GIF"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setUploadingPhoto(true);
    try {
      const compressed = await compressImage(file);
      const form = new FormData();
      form.append("file", compressed, "photo.jpg");
      const res = await axios.post(`${API}/admin/brief/photo`, form, { headers: { ...authHeader(), "Content-Type": "multipart/form-data" } });
      setBriefPhotoUrl(`${API}/brief/photo/${res.data.path}`);
      toast.success("Photo uploaded!");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed — try a smaller image or paste a URL below"); }
    finally { setUploadingPhoto(false); if (photoInputRef.current) photoInputRef.current.value = ""; }
  };

  return {
    // State
    brief, briefContent, setBriefContent, briefPhotoUrl, setBriefPhotoUrl,
    briefPublished, setBriefPublished, generatingBrief, savingBrief, loadingBrief,
    uploadingPhoto, socialTexts, setSocialTexts, briefOgPrompt, setBriefOgPrompt,
    regeneratingBriefOg, autoSettings, setAutoSettings, savingAutoSettings,
    editorComment, setEditorComment, editorUrls, setEditorUrls, newEditorUrl,
    setNewEditorUrl, editorNotesActive, savingEditorNotes, loadingEditorNotes,
    addingEditorUrl,
    photoInputRef,
    // Brief Featured queue
    briefFeatured, loadingBriefFeatured,
    loadBriefFeatured, removeBriefFeatured, clearBriefFeatured,
    // Handlers
    loadBrief, loadEditorNotes, saveEditorNotes, clearEditorNotes, addEditorUrl,
    saveAutoSettings, generateBrief, saveBrief, saveDraft, regenerateBriefOg, uploadBriefPhoto,
  };
}
